import React from 'react';
import {useTranslation} from "react-i18next";
import {Box} from "@mui/material";
import {ActionsMenu} from "@dreebit-registry/vsm-microservice-components";
import {AddRounded, DescriptionRounded, EditRounded, PrintRounded} from "@mui-symbols-material/w200";
import {EventCategory, EventType} from "../../codegen/graphql";
import {isContaminationDeclarationsEnabled} from "../../utils/contaminationDeclarationsEnabled";
import {useDeclarationForEvent} from "../../hooks/useDeclarationForEvent";
import {useOpenContaminationDeclaration} from "../../hooks/useOpenContaminationDeclaration";
import {useContaminationDeclarationPrint} from "../../hooks/useContaminationDeclarationPrint";

type ActionItems = React.ComponentProps<typeof ActionsMenu>['items'];

interface Props {
    eventId: string;
    eventType?: string | null;
    eventCategory?: string | null;
    /** Present when the user may edit lifecycle events. */
    onEditEvent?: (id: string | number) => void;
    /** Offer the declaration action (per-asset history with the DoC feature on). */
    withDeclaration?: boolean;
}

/**
 * Row actions of the event history: "Edit" for lifecycle events and, on
 * ASSET_REMOVE rows, the same create/open declaration action the DoC status
 * chip performs, plus "Print declaration" once the declaration is printable.
 * The lookup is shared with the chip through Apollo's cache, so the extra menu
 * entries cost no further request.
 */
const EventRowActions = ({eventId, eventType, eventCategory, onEditEvent, withDeclaration}: Props) => {
    const {t} = useTranslation();
    const declarationEnabled = !!withDeclaration && isContaminationDeclarationsEnabled() && eventType === EventType.ASSET_REMOVE;
    const {declaration, readiness, available, loading, error} = useDeclarationForEvent(eventId, !declarationEnabled);
    const {openForEvent, busy, canCreate} = useOpenContaminationDeclaration();
    const {print, busy: printing} = useContaminationDeclarationPrint();
    // Starting is gated on the owning client's region; opening an existing one is not.
    const canStart = canCreate && available;

    const items: ActionItems = [];

    // Only lifecycle events carry editable TDAs; other categories never have any.
    if (onEditEvent && eventCategory === EventCategory.LIFECYCLE) {
        items.push({
            item: (
                <>
                    <EditRounded fontSize={"inherit"} sx={{mr: "4px"}}/>
                    {t('Edit')}
                </>
            ),
            onClick: () => onEditEvent(eventId),
            active: true,
        });
    }

    // Silent while loading or without node access (same rule as the status chip).
    if (declarationEnabled && !loading && !error && (declaration || canStart)) {
        items.push({
            item: declaration ? (
                <>
                    <DescriptionRounded fontSize={"inherit"} sx={{mr: "4px"}}/>
                    {t('Open declaration')}
                </>
            ) : (
                <>
                    <AddRounded fontSize={"inherit"} sx={{mr: "4px"}}/>
                    {t('Create declaration')}
                </>
            ),
            onClick: () => { openForEvent(eventId); },
            active: true,
            disabled: busy,
        });

        // ActionsMenu calls onClick synchronously, so the click's user
        // activation still stands when the print hook opens its tab.
        if (declaration && readiness?.ready) {
            items.push({
                item: (
                    <>
                        <PrintRounded fontSize={"inherit"} sx={{mr: "4px"}}/>
                        {t('Print declaration')}
                    </>
                ),
                onClick: () => { print(declaration.id); },
                active: true,
                disabled: printing,
            });
        }
    }

    if (items.length === 0) return null;

    // Stop the click from bubbling to the grid's onRowClick so opening the
    // actions menu never toggles the detail drawer.
    return (
        <Box onClick={(e) => e.stopPropagation()} data-testid="event-row-actions">
            <ActionsMenu items={items}/>
        </Box>
    );
};

export default EventRowActions;
